import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getEdit,
  createEdit,
  saveEdit,
  publishEdit,
  unpublishEdit,
} from "./adminApi";
import { AdminHeader } from "./AdminDashboard";
import { LibraryPicker } from "./LibraryPicker";
import {
  LIST_SECTIONS,
  SECTION_ORDER,
  SECTION_META,
  LIST_FIELDS,
  ITEM_TITLE,
  newItem,
  uid,
} from "./tripBuilderConfig";

const inputCls =
  "w-full rounded-none border border-charcoal bg-charcoal/30 px-3 py-2 font-sans text-sm text-cream placeholder:text-sand/50 focus:border-teal focus:outline-none";
const labelCls = "font-sans text-[0.62rem] uppercase tracking-[0.18em] text-sand";

const TextField = ({ label, value, onChange, placeholder, textarea, testid }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {textarea ? (
      <textarea
        rows={2}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className={`${inputCls} mt-1 resize-none`}
      />
    ) : (
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className={`${inputCls} mt-1`}
      />
    )}
  </div>
);

// A single item card within a list section
const ItemCard = ({ section, item, index, total, onField, onMove, onRemove }) => {
  const fields = LIST_FIELDS[section];
  return (
    <div className="border border-charcoal bg-ink p-4" data-testid={`${section}-item-${index}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="truncate font-sans text-xs uppercase tracking-[0.16em] text-teal">
          {ITEM_TITLE[section](item)}
        </p>
        <div className="flex shrink-0 items-center gap-1">
          <button
            onClick={() => onMove(index, -1)}
            disabled={index === 0}
            title="Move up"
            data-testid={`${section}-up-${index}`}
            className="border border-charcoal px-2 py-1 font-sans text-xs text-sand hover:border-teal hover:text-teal disabled:opacity-30"
          >
            ↑
          </button>
          <button
            onClick={() => onMove(index, 1)}
            disabled={index === total - 1}
            title="Move down"
            data-testid={`${section}-down-${index}`}
            className="border border-charcoal px-2 py-1 font-sans text-xs text-sand hover:border-teal hover:text-teal disabled:opacity-30"
          >
            ↓
          </button>
          <button
            onClick={() => onRemove(index)}
            title="Remove"
            data-testid={`${section}-remove-${index}`}
            className="border border-charcoal px-2 py-1 font-sans text-xs text-sand hover:border-red-400 hover:text-red-400"
          >
            ✕
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : f.w === "sm" ? "" : "sm:col-span-2"}>
            <TextField
              label={f.label}
              value={item[f.key]}
              placeholder={f.placeholder}
              textarea={f.type === "textarea"}
              onChange={(v) => onField(index, f.key, v)}
              testid={`${section}-${f.key}-${index}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionShell = ({ id, visible, onToggle, children }) => {
  const meta = SECTION_META[id];
  return (
    <section className="border-t border-charcoal py-8" data-testid={`section-${id}`}>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-light text-cream">{meta.label}</h2>
          <p className="mt-1 font-sans text-xs text-sand">{meta.blurb}</p>
        </div>
        <label className="flex shrink-0 cursor-pointer items-center gap-2" title="Show to customer">
          <span className={labelCls}>Show to customer</span>
          <input
            type="checkbox"
            checked={!!visible}
            onChange={(e) => onToggle(id, e.target.checked)}
            data-testid={`toggle-${id}`}
            className="h-4 w-4 accent-teal"
          />
        </label>
      </div>
      <div className={visible ? "" : "opacity-40"}>{children}</div>
    </section>
  );
};

export const AdminTripBuilder = () => {
  const { id } = useParams();
  const [edit, setEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState("");
  const [pickerSection, setPickerSection] = useState(null);

  const publicUrl = edit ? `${window.location.origin}/trip/${edit.secure_token}` : "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const e = await getEdit(id);
      setEdit(e);
      setNotFound(false);
    } catch (err) {
      if (err.message && err.message.includes("No edit")) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const onCreate = async () => {
    setSaving(true);
    try {
      setEdit(await createEdit(id));
      setNotFound(false);
    } finally {
      setSaving(false);
    }
  };

  // --- state mutators ---
  const setField = (key, value) => {
    setEdit((e) => ({ ...e, [key]: value }));
    setDirty(true);
  };
  const setBudgetField = (key, value) => {
    setEdit((e) => ({ ...e, budget: { ...(e.budget || {}), [key]: value } }));
    setDirty(true);
  };
  const toggleSection = (section, val) => {
    setEdit((e) => ({ ...e, section_visibility: { ...(e.section_visibility || {}), [section]: val } }));
    setDirty(true);
  };
  const addItem = (section) => {
    setEdit((e) => ({ ...e, [section]: [...(e[section] || []), newItem(section)] }));
    setDirty(true);
  };
  // Pull a verified record from the Knowledge Library into a section item (source preserved via library_id).
  const mapRecToItem = (section, rec) => {
    const note = [
      rec.holideviser_take,
      rec.know_before_you_go ? `Know before you go: ${rec.know_before_you_go}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");
    const area = rec.neighborhood || rec.destination || "";
    const base = { id: uid(), library_id: rec.id, name: rec.name, area, note };
    if (section === "eats") return { ...base, cuisine: rec.subcategory || "", meal: rec.best_moment || "" };
    return { ...base, type: rec.subcategory || "" };
  };
  const addFromLibrary = (section, rec) => {
    setEdit((e) => ({ ...e, [section]: [...(e[section] || []), mapRecToItem(section, rec)] }));
    setDirty(true);
    setPickerSection(null);
    flash(`Added ${rec.name} from Library`);
  };
  const removeItem = (section, index) => {
    setEdit((e) => ({ ...e, [section]: (e[section] || []).filter((_, i) => i !== index) }));
    setDirty(true);
  };
  const fieldItem = (section, index, key, val) => {
    setEdit((e) => ({
      ...e,
      [section]: (e[section] || []).map((it, i) => (i === index ? { ...it, [key]: val } : it)),
    }));
    setDirty(true);
  };
  const moveItem = (section, index, dir) => {
    setEdit((e) => {
      const arr = [...(e[section] || [])];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return e;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      return { ...e, [section]: arr };
    });
    setDirty(true);
  };

  // Budget summary line items
  const addBudgetLine = () => {
    setEdit((e) => ({
      ...e,
      budget: {
        ...(e.budget || {}),
        customer_summary: [...((e.budget || {}).customer_summary || []), { id: uid(), label: "", amount: "", note: "" }],
      },
    }));
    setDirty(true);
  };
  const budgetLineField = (index, key, val) => {
    setEdit((e) => ({
      ...e,
      budget: {
        ...(e.budget || {}),
        customer_summary: ((e.budget || {}).customer_summary || []).map((it, i) =>
          i === index ? { ...it, [key]: val } : it
        ),
      },
    }));
    setDirty(true);
  };
  const removeBudgetLine = (index) => {
    setEdit((e) => ({
      ...e,
      budget: {
        ...(e.budget || {}),
        customer_summary: ((e.budget || {}).customer_summary || []).filter((_, i) => i !== index),
      },
    }));
    setDirty(true);
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: edit.title,
        subtitle: edit.subtitle,
        point_of_view: edit.point_of_view,
        hero_image: edit.hero_image,
        traveller_name: edit.traveller_name,
        destination_label: edit.destination_label,
        trip_dates_label: edit.trip_dates_label,
        duration_label: edit.duration_label,
        route: edit.route,
        stays: edit.stays,
        eats: edit.eats,
        drinks: edit.drinks,
        experiences: edit.experiences,
        skips: edit.skips,
        daily_rhythm: edit.daily_rhythm,
        practicals: edit.practicals,
        budget: edit.budget,
        section_visibility: edit.section_visibility,
        internal_notes: edit.internal_notes,
      };
      const updated = await saveEdit(edit.id, payload);
      setEdit(updated);
      setDirty(false);
      flash("Draft saved");
    } catch (err) {
      flash(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doPublish = async () => {
    setSaving(true);
    try {
      if (dirty) await doSave();
      setEdit(await publishEdit(edit.id));
      flash("Published — link is live");
    } catch (err) {
      flash(err.message || "Publish failed");
    } finally {
      setSaving(false);
    }
  };

  const doUnpublish = async () => {
    setSaving(true);
    try {
      setEdit(await unpublishEdit(edit.id));
      flash("Unpublished — link is now private");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      flash("Private link copied");
    } catch {
      flash("Copy failed — select the link manually");
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-ink text-cream">
        <AdminHeader />
        <p className="p-10 font-sans text-sm text-sand">Loading the Edit…</p>
      </div>
    );

  if (notFound)
    return (
      <div className="min-h-screen bg-ink text-cream">
        <AdminHeader />
        <main className="mx-auto max-w-[900px] px-6 py-16 text-center sm:px-10">
          <Link
            to={`/admin/trips/${id}`}
            className="font-sans text-xs uppercase tracking-[0.18em] text-sand hover:text-teal"
          >
            ← Back to request
          </Link>
          <h1 className="mt-10 font-serif text-3xl font-light text-cream">Start The Holideviser Edit</h1>
          <p className="mx-auto mt-4 max-w-md font-sans text-sm text-sand">
            Build a personalised, editorial trip plan for this traveller. You curate every section by hand —
            nothing is auto-generated.
          </p>
          <button
            onClick={onCreate}
            disabled={saving}
            data-testid="create-edit-btn"
            className="mt-8 rounded-none bg-teal px-8 py-3 font-sans text-xs uppercase tracking-[0.2em] text-ink hover:bg-cream disabled:opacity-60"
          >
            {saving ? "Creating…" : "Create the Edit"}
          </button>
        </main>
      </div>
    );

  const vis = edit.section_visibility || {};
  const isPublished = edit.status === "PUBLISHED";

  return (
    <div className="min-h-screen overflow-x-hidden bg-ink text-cream" data-testid="admin-trip-builder">
      <AdminHeader />

      {/* Sticky action bar */}
      <div className="sticky top-0 z-30 border-b border-charcoal bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1100px] flex-wrap items-center justify-between gap-3 px-6 py-3 sm:px-10">
          <div className="flex items-center gap-3">
            <Link
              to={`/admin/trips/${id}`}
              className="font-sans text-xs uppercase tracking-[0.18em] text-sand hover:text-teal"
            >
              ← Request
            </Link>
            <span
              data-testid="edit-status-badge"
              className={`border px-2 py-1 font-sans text-[0.6rem] uppercase tracking-[0.18em] ${
                isPublished ? "border-teal text-teal" : "border-sand/40 text-sand"
              }`}
            >
              {isPublished ? "Published" : "Draft"}
            </span>
            {dirty && <span className="font-sans text-[0.6rem] text-sand">unsaved changes</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={doSave}
              disabled={saving}
              data-testid="save-draft-btn"
              className="rounded-none border border-cream px-4 py-2 font-sans text-xs uppercase tracking-[0.16em] text-cream hover:bg-cream hover:text-ink disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save draft"}
            </button>
            {isPublished ? (
              <button
                onClick={doUnpublish}
                disabled={saving}
                data-testid="unpublish-btn"
                className="rounded-none border border-sand/50 px-4 py-2 font-sans text-xs uppercase tracking-[0.16em] text-sand hover:border-red-400 hover:text-red-400 disabled:opacity-60"
              >
                Unpublish
              </button>
            ) : (
              <button
                onClick={doPublish}
                disabled={saving}
                data-testid="publish-btn"
                className="rounded-none bg-teal px-4 py-2 font-sans text-xs uppercase tracking-[0.16em] text-ink hover:bg-cream disabled:opacity-60"
              >
                Publish
              </button>
            )}
          </div>
        </div>
        {isPublished && (
          <div className="mx-auto flex max-w-[1100px] flex-wrap items-center gap-3 px-6 pb-3 sm:px-10">
            <span className={labelCls}>Private link</span>
            <code
              data-testid="public-link"
              className="max-w-full flex-1 truncate rounded-none border border-charcoal bg-charcoal/30 px-3 py-1.5 font-sans text-xs text-teal"
            >
              {publicUrl}
            </code>
            <button
              onClick={copyLink}
              data-testid="copy-link-btn"
              className="rounded-none border border-teal px-3 py-1.5 font-sans text-[0.65rem] uppercase tracking-[0.16em] text-teal hover:bg-teal hover:text-ink"
            >
              Copy
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="preview-link-btn"
              className="rounded-none border border-charcoal px-3 py-1.5 font-sans text-[0.65rem] uppercase tracking-[0.16em] text-sand hover:border-teal hover:text-teal"
            >
              Preview ↗
            </a>
          </div>
        )}
      </div>

      <main className="mx-auto max-w-[1100px] px-6 py-4 sm:px-10">
        {/* OVERVIEW */}
        <SectionShell id="overview" visible={vis.overview} onToggle={toggleSection}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <TextField label="Title" value={edit.title} onChange={(v) => setField("title", v)} placeholder="e.g. Central Vietnam, Slowly" testid="edit-title" />
            </div>
            <div className="sm:col-span-2">
              <TextField label="Subtitle" value={edit.subtitle} onChange={(v) => setField("subtitle", v)} placeholder="A one-line framing" testid="edit-subtitle" />
            </div>
            <TextField label="Traveller name" value={edit.traveller_name} onChange={(v) => setField("traveller_name", v)} testid="edit-traveller" />
            <TextField label="Destination label" value={edit.destination_label} onChange={(v) => setField("destination_label", v)} testid="edit-destination" />
            <TextField label="Trip dates" value={edit.trip_dates_label} onChange={(v) => setField("trip_dates_label", v)} placeholder="e.g. Late Nov, 8 nights" testid="edit-dates" />
            <TextField label="Duration label" value={edit.duration_label} onChange={(v) => setField("duration_label", v)} placeholder="e.g. 8 days / 7 nights" testid="edit-duration" />
            <div className="sm:col-span-2">
              <TextField label="Hero image URL (optional)" value={edit.hero_image} onChange={(v) => setField("hero_image", v)} placeholder="https://…" testid="edit-hero" />
            </div>
            <div className="sm:col-span-2">
              <TextField label="Point of view (the opening editorial note)" value={edit.point_of_view} onChange={(v) => setField("point_of_view", v)} textarea testid="edit-pov" />
            </div>
          </div>
        </SectionShell>

        {/* LIST SECTIONS */}
        {LIST_SECTIONS.map((section) => (
          <SectionShell key={section} id={section} visible={vis[section]} onToggle={toggleSection}>
            <div className="flex flex-col gap-4">
              {(edit[section] || []).map((item, i) => (
                <ItemCard
                  key={item.id || i}
                  section={section}
                  item={item}
                  index={i}
                  total={(edit[section] || []).length}
                  onField={(idx, key, val) => fieldItem(section, idx, key, val)}
                  onMove={(idx, dir) => moveItem(section, idx, dir)}
                  onRemove={(idx) => removeItem(section, idx)}
                />
              ))}
              {(edit[section] || []).length === 0 && (
                <p className="font-sans text-xs italic text-sand">Nothing added yet.</p>
              )}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => addItem(section)}
                  data-testid={`add-${section}`}
                  className="self-start rounded-none border border-dashed border-charcoal px-4 py-2 font-sans text-xs uppercase tracking-[0.16em] text-sand hover:border-teal hover:text-teal"
                >
                  + Add {SECTION_META[section].label.toLowerCase()}
                </button>
                {(section === "eats" || section === "drinks") && (
                  <button
                    onClick={() => setPickerSection(section)}
                    data-testid={`add-from-library-${section}`}
                    className="self-start rounded-none border border-teal px-4 py-2 font-sans text-xs uppercase tracking-[0.16em] text-teal hover:bg-teal hover:text-ink"
                  >
                    + Add from Holideviser Library
                  </button>
                )}
              </div>
            </div>
          </SectionShell>
        ))}

        {/* BUDGET */}
        <SectionShell id="budget" visible={vis.budget} onToggle={toggleSection}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <TextField label="Currency" value={(edit.budget || {}).currency} onChange={(v) => setBudgetField("currency", v)} placeholder="INR" testid="budget-currency" />
          </div>

          <p className="mt-6 font-sans text-[0.62rem] uppercase tracking-[0.18em] text-teal">
            Customer-facing summary
          </p>
          <div className="mt-3 flex flex-col gap-3">
            {((edit.budget || {}).customer_summary || []).map((line, i) => (
              <div key={line.id || i} className="grid grid-cols-1 items-end gap-3 border border-charcoal bg-ink p-3 sm:grid-cols-[1fr_1fr_2fr_auto]" data-testid={`budget-line-${i}`}>
                <TextField label="Item" value={line.label} onChange={(v) => budgetLineField(i, "label", v)} placeholder="e.g. Stays" testid={`budget-label-${i}`} />
                <TextField label="Amount" value={line.amount} onChange={(v) => budgetLineField(i, "amount", v)} placeholder="e.g. ₹90,000" testid={`budget-amount-${i}`} />
                <TextField label="Note" value={line.note} onChange={(v) => budgetLineField(i, "note", v)} placeholder="Optional detail" testid={`budget-note-${i}`} />
                <button
                  onClick={() => removeBudgetLine(i)}
                  data-testid={`budget-remove-${i}`}
                  className="h-[38px] border border-charcoal px-3 font-sans text-xs text-sand hover:border-red-400 hover:text-red-400"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              onClick={addBudgetLine}
              data-testid="add-budget-line"
              className="self-start rounded-none border border-dashed border-charcoal px-4 py-2 font-sans text-xs uppercase tracking-[0.16em] text-sand hover:border-teal hover:text-teal"
            >
              + Add budget line
            </button>
          </div>

          <div className="mt-4">
            <TextField
              label="Total / summary note (customer-facing)"
              value={(edit.budget || {}).customer_total_note}
              onChange={(v) => setBudgetField("customer_total_note", v)}
              textarea
              placeholder="e.g. Roughly ₹2.4L for two, excluding flights"
              testid="budget-total-note"
            />
          </div>

          <div className="mt-5 border border-red-400/30 bg-red-400/[0.05] p-4">
            <TextField
              label="Internal budget notes (never shown to customer)"
              value={(edit.budget || {}).internal_notes}
              onChange={(v) => setBudgetField("internal_notes", v)}
              textarea
              placeholder="Margins, supplier costs, private reasoning…"
              testid="budget-internal-notes"
            />
          </div>
        </SectionShell>

        {/* INTERNAL NOTES */}
        <section className="border-t border-charcoal py-8">
          <h2 className="font-serif text-2xl font-light text-cream">Internal notes</h2>
          <p className="mt-1 font-sans text-xs text-sand">Private to the team. Never shown on the customer page.</p>
          <div className="mt-4 border border-red-400/30 bg-red-400/[0.05] p-4">
            <TextField
              label="Notes"
              value={edit.internal_notes}
              onChange={(v) => setField("internal_notes", v)}
              textarea
              testid="edit-internal-notes"
            />
          </div>
        </section>

        <div className="border-t border-charcoal py-8">
          <button
            onClick={doSave}
            disabled={saving}
            data-testid="save-draft-bottom"
            className="rounded-none border border-cream px-6 py-3 font-sans text-xs uppercase tracking-[0.16em] text-cream hover:bg-cream hover:text-ink disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save draft"}
          </button>
        </div>
      </main>

      {pickerSection && (
        <LibraryPicker
          category={pickerSection === "eats" ? "EAT" : "DRINK"}
          onPick={(rec) => addFromLibrary(pickerSection, rec)}
          onClose={() => setPickerSection(null)}
        />
      )}

      {toast && (
        <div
          data-testid="builder-toast"
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 border border-teal bg-ink px-5 py-3 font-sans text-xs uppercase tracking-[0.16em] text-teal shadow-lg"
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default AdminTripBuilder;
